import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AscendingK from '../components/AscendingK';
import { Icons } from '../components/Icons';
import { useAuth } from '../context/AuthContext';
import { getApiBaseUrl } from '../lib/api';
import { isPhoneChar, validateEmail, validatePassword, validatePhone } from '../utils/validation';
import '../styles/app.css';

export default function AuthPage() {
  const navigate = useNavigate();
  const { login, socialLogin } = useAuth();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [socialProvider, setSocialProvider] = useState('');
  const [providerConfig, setProviderConfig] = useState({
    googleClientId: '',
    facebookAppId: ''
  });
  const [configReady, setConfigReady] = useState(false);
  const googleScriptPromise = useRef(null);
  const facebookScriptPromise = useRef(null);
  const googleButtonRef = useRef(null);
  const googleInitializedRef = useRef(false);

  const googleClientId = providerConfig.googleClientId || import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
  const facebookAppId = providerConfig.facebookAppId || import.meta.env.VITE_FACEBOOK_APP_ID || '';

  useEffect(() => {
    let active = true;
    fetch(`${getApiBaseUrl()}/public-config`, { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!active || !data) return;
        setProviderConfig({
          googleClientId: data.googleClientId || '',
          facebookAppId: data.facebookAppId || ''
        });
      })
      .catch(() => {})
      .finally(() => {
        if (active) setConfigReady(true);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const nextMode = hash.get('mode');
    if (nextMode === 'signup' || nextMode === 'login') {
      setMode(nextMode);
    }
  }, []);

  useEffect(() => () => {
    googleInitializedRef.current = false;
  }, []);

  const loadScript = (promiseRef, src, check) => {
    if (check()) {
      return Promise.resolve();
    }
    if (promiseRef.current) {
      return promiseRef.current;
    }
    promiseRef.current = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Unable to load provider script')), { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Unable to load provider script'));
      document.head.appendChild(script);
    });

    return promiseRef.current;
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    const nextErrors = {};
    if (!validateEmail(email)) nextErrors.email = 'Please enter a valid email address';
    if (!validatePassword(password)) nextErrors.password = 'Password must be at least 8 characters';
    if (mode === 'signup') {
      if (!name.trim()) nextErrors.name = 'Name is required';
      if (!validatePhone(phone)) nextErrors.phone = 'Please enter a valid phone number';
    }
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    try {
      setSubmitting(true);
      setFormError('');
      await login({ name, email, phone, password }, mode);
      navigate('/app', { replace: true });
    } catch (error) {
      setFormError(error.message || 'Unable to continue');
    } finally {
      setSubmitting(false);
    }
  };

  const runSocialLogin = async (provider, providerToken) => {
    try {
      setSubmitting(true);
      setFormError('');
      setSocialProvider(provider);
      await socialLogin(provider, providerToken);
      navigate('/app', { replace: true });
    } catch (error) {
      setFormError(error.message || 'Social sign-in failed');
    } finally {
      setSubmitting(false);
      setSocialProvider('');
    }
  };

  const renderGoogleButton = async () => {
    if (!googleClientId) {
      setFormError('Google sign-in is not configured.');
      return;
    }

    try {
      await loadScript(googleScriptPromise, 'https://accounts.google.com/gsi/client', () => Boolean(window.google?.accounts?.id));
      if (!googleButtonRef.current || googleInitializedRef.current) {
        return;
      }
      googleInitializedRef.current = true;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response) => {
          if (response?.credential) {
            await runSocialLogin('google', response.credential);
          }
        }
      });
      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'signin_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        width: googleButtonRef.current.clientWidth || 360
      });
    } catch (error) {
      setSubmitting(false);
      setSocialProvider('');
      setFormError(error.message || 'Google sign-in failed');
    }
  };

  const handleMetaLogin = async () => {
    if (!facebookAppId) {
      setFormError('Meta sign-in is not configured.');
      return;
    }

    try {
      setFormError('');
      setSubmitting(true);
      setSocialProvider('facebook');
      await loadScript(facebookScriptPromise, 'https://connect.facebook.net/en_US/sdk.js', () => Boolean(window.FB?.init));
      window.FB.init({
        appId: facebookAppId,
        cookie: false,
        xfbml: false,
        version: 'v21.0'
      });
      window.FB.login(async (response) => {
        if (response?.authResponse?.accessToken) {
          await runSocialLogin('facebook', response.authResponse.accessToken);
        } else {
          setFormError('Meta sign-in was cancelled.');
          setSubmitting(false);
          setSocialProvider('');
        }
      }, { scope: 'email,public_profile' });
    } catch (error) {
      setSubmitting(false);
      setSocialProvider('');
      setFormError(error.message || 'Meta sign-in failed');
    }
  };

  const handleAppleLogin = () => {
    setFormError('Apple Sign in is not configured yet. It needs an Apple Developer Services ID, Team ID, Key ID, and a private key.');
  };

  useEffect(() => {
    if (googleClientId && configReady) {
      renderGoogleButton();
    }
  }, [googleClientId, configReady]);

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <Link to="/" style={{ cursor: 'pointer', display: 'inline-block' }}>
            <AscendingK size={36} />
          </Link>
          <h1 className="auth-title">
            {mode === 'login'
              ? <>Welcome <em>back</em>.</>
              : <>Create your <em>card</em>.</>}
          </h1>
          <p className="auth-subtitle">
            {mode === 'login'
              ? 'Sign in to manage your digital card.'
              : 'Join the network. It takes under two minutes.'}
          </p>
        </div>

      <div className="auth-card">
        {googleClientId ? (
          <div
            className="social-btn social-btn-google-slot"
            ref={googleButtonRef}
            aria-label="Continue with Google"
            style={{ minHeight: 48 }}
          />
        ) : (
          <button className="social-btn" type="button" disabled>
            {Icons.google}
            <span>{configReady ? 'Google sign-in unavailable' : 'Loading Google sign-in...'}</span>
          </button>
        )}

          <button className="social-btn" onClick={handleAppleLogin} type="button" disabled={submitting}>
            {Icons.apple}
            <span>Continue with Apple</span>
          </button>
          <button className="social-btn" onClick={handleMetaLogin} type="button" disabled={submitting || !facebookAppId}>
            {Icons.meta}
            <span>{socialProvider === 'facebook' ? 'Connecting Meta...' : facebookAppId ? 'Continue with Meta' : 'Meta sign-in unavailable'}</span>
          </button>

          <div className="auth-divider">or use email</div>

          <form onSubmit={handleSubmit} noValidate>
            {mode === 'signup' && (
              <>
                <div className="form-group">
                  <label className="form-label">Full Name <span className="required">*</span></label>
                  <input
                    className="form-input"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                  {errors.name && <div className="form-error">{errors.name}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Phone Number <span className="required">*</span></label>
                  <input
                    className="form-input"
                    placeholder="+49 152 5781 0090"
                    value={phone}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      if (isPhoneChar(nextValue)) {
                        setPhone(nextValue);
                      }
                    }}
                  />
                  {errors.phone && <div className="form-error">{errors.phone}</div>}
                </div>
              </>
            )}

            <div className="form-group">
              <label className="form-label">Email <span className="required">*</span></label>
              <input
                className="form-input"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                inputMode="email"
                autoCapitalize="none"
                spellCheck={false}
              />
              {errors.email && <div className="form-error">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Password <span className="required">*</span></label>
              <input
                className="form-input"
                type="password"
                placeholder="Min 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                autoCapitalize="none"
                spellCheck={false}
              />
              {errors.password && <div className="form-error">{errors.password}</div>}
            </div>

            {formError && <div className="form-error" style={{ marginBottom: 12 }}>{formError}</div>}

            <button
              className="btn btn-primary-app btn-full"
              style={{ marginTop: 8, padding: 14 }}
              type="submit"
              disabled={submitting}
            >
              {submitting ? 'Please wait...' : mode === 'login' ? 'Sign in' : 'Create account'}
            </button>
          </form>
        </div>

        <div className="auth-footer">
          {mode === 'login' ? (
            <>Don&rsquo;t have an account? <span className="auth-link" onClick={() => setMode('signup')}>Create one</span></>
          ) : (
            <>Already have an account? <span className="auth-link" onClick={() => setMode('login')}>Sign in</span></>
          )}
        </div>
      </div>
    </div>
  );
}
